import {
  AttentionBlock,
  Button,
  Checkbox,
  ChoiceRow,
  EmptyState,
  Field,
  FormBlock,
  Input,
  ListRow,
  ListRows,
  SearchInput,
  Skeleton,
  StatusTag,
  plural,
} from '@westhetics/ui'
import { useState } from 'react'
import { formatDocNumber } from '@/features/clinic-profile/doc-number'
import type { FormController } from '@/features/clinic-profile/use-form-state'
import { useClinics, type AdminClinic } from '@/features/clinics/api'
import { errorMessage } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import type { NetworkClinic } from './api'
import { clinicLabel, type NetworkFormState } from './form'

/**
 * Os blocos do cadastro da rede (docs/areas/administracao.md, "Nova rede" e "Detalhe da rede";
 * RN-ADM-54, RT-06), os mesmos na criação e no detalhe. Nome, compartilhamento e composição são
 * salvos juntos pelo Salvar do cabeçalho, com o conjunto completo de clínicas (P-ADM-80). O estado
 * e as regras do formulário estão em `form.ts`.
 */

type NetworkForm = FormController<NetworkFormState>

export function NetworkIdentificationBlock({ form }: { form: NetworkForm }) {
  return (
    <FormBlock
      title="Identificação"
      note="O nome aparece na lista de clínicas e no cadastro de cada clínica da rede."
    >
      <Field label="Nome" error={form.errors.name} hint="Como Franquia São Paulo.">
        <Input
          autoComplete="off"
          maxLength={80}
          value={form.values.name}
          onChange={(event) => form.update({ name: event.target.value })}
          onBlur={() => form.touch('name')}
        />
      </Field>
    </FormBlock>
  )
}

/**
 * A única opção da rede (RT-06). O efeito está escrito no campo e, enquanto a mudança não foi salva,
 * em lilás; não há modal (P-ADM-80).
 */
export function NetworkSharingBlock({
  form,
  savedValue,
}: {
  form: NetworkForm
  /** O compartilhamento como está salvo; sem ele (criação), nada está salvo ainda. */
  savedValue?: boolean
}) {
  const { sharesPatients, clinics } = form.values
  const changed = savedValue !== undefined && savedValue !== sharesPatients
  const clinicCount = clinics.length
  return (
    <FormBlock title="Compartilhamento" note="O que as clínicas da rede enxergam umas das outras.">
      <ChoiceRow
        control={
          <Checkbox
            checked={sharesPatients}
            onCheckedChange={(checked) => form.update({ sharesPatients: checked === true })}
          />
        }
        description="Quem tem vínculo em uma clínica da rede lê pacientes, anamneses, documentos e planos das outras, sempre com a clínica de origem indicada. Ninguém escreve na clínica da outra."
      >
        Leitura compartilhada de pacientes
      </ChoiceRow>
      <p className="font-body text-eyebrow text-label mt-3 leading-[1.5] font-light tracking-normal">
        Procedimentos, prestadores, usuários e documentos não são compartilhados: cada clínica tem
        os seus.
      </p>
      {changed ? (
        // Lilás: é condicional, não risco (o compartilhamento é reversível).
        <AttentionBlock className="mt-4" variant="lilac" tone="pampas" title="Ao salvar">
          <p>
            {sharesPatients
              ? clinicCount > 1
                ? `As ${plural(clinicCount, 'clínica')} da rede passam a ler os pacientes umas das outras.`
                : 'As clínicas que entrarem na rede passam a ler os pacientes umas das outras.'
              : 'Cada clínica volta a ler só os próprios pacientes.'}
          </p>
        </AttentionBlock>
      ) : null}
    </FormBlock>
  )
}

/** "Aurora Estética Avançada Ltda · Curitiba/PR" (a razão social só quando o título é o nome fantasia). */
function clinicMeta(clinic: NetworkClinic, docNumber?: string) {
  return [
    clinic.tradeName ? clinic.legalName : null,
    docNumber ?? null,
    // A clínica de cadastro próprio pode ainda não ter endereço (RN-ADM-89).
    clinic.city ? `${clinic.city}/${clinic.state}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * A composição da rede: as clínicas que ficam, com "remover", e a busca entre as clínicas sem rede
 * (`GET /admin/clinics?filter=no_network`) para adicionar uma a uma (P-ADM-81). A clínica que já
 * está em outra rede não aparece como opção: sai de lá antes (RN-ADM-54).
 */
export function NetworkClinicsBlock({ form, error }: { form: NetworkForm; error?: string }) {
  const { clinics } = form.values
  const [adding, setAdding] = useState(false)

  function remove(clinic: NetworkClinic) {
    form.update({ clinics: clinics.filter((item) => item.id !== clinic.id) })
  }

  function add(clinic: AdminClinic) {
    if (clinics.some((item) => item.id === clinic.id)) return
    form.update({
      clinics: [
        ...clinics,
        {
          id: clinic.id,
          legalName: clinic.legalName,
          tradeName: clinic.tradeName,
          city: clinic.address?.city ?? '',
          state: clinic.address?.state ?? '',
          status: clinic.status,
        },
      ],
    })
  }

  return (
    <FormBlock
      title={
        <>
          Clínicas
          <span className="text-label-soft tracking-normal normal-case"> · opcional</span>
        </>
      }
      note="Uma clínica pertence a uma rede só. Quem está em outra rede sai de lá antes."
    >
      {clinics.length === 0 ? (
        <EmptyState
          title="Nenhuma clínica na rede"
          description="Rede sem clínica não compartilha nada: equivale a desativada. Adicione a primeira para a rede valer."
        />
      ) : (
        <ListRows role="list" aria-label="Clínicas da rede">
          {clinics.map((clinic) => (
            <ListRow
              key={clinic.id}
              role="listitem"
              title={clinicLabel(clinic)}
              meta={clinicMeta(clinic)}
              trailing={
                <>
                  <StatusTag level={clinic.status === 'blocked' ? 'attention' : 'done'}>
                    {clinic.status === 'blocked' ? 'Bloqueada' : 'Ativa'}
                  </StatusTag>
                  <button
                    type="button"
                    onClick={() => remove(clinic)}
                    className="font-body text-eyebrow text-label hover:text-ink max-desk:min-h-touch cursor-pointer font-light tracking-normal"
                  >
                    remover
                  </button>
                </>
              }
            />
          ))}
        </ListRows>
      )}

      {error ? (
        <p
          role="alert"
          className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
        >
          {error}
        </p>
      ) : null}

      <div className="mt-4">
        {adding ? (
          <ClinicPicker
            excludedIds={clinics.map((clinic) => clinic.id)}
            onPick={add}
            onClose={() => setAdding(false)}
          />
        ) : (
          <Button
            className="max-desk:min-h-touch"
            variant="outline"
            onClick={() => setAdding(true)}
          >
            + Adicionar clínica
          </Button>
        )}
      </div>

      <p className="font-body text-eyebrow text-label mt-4 leading-[1.5] font-light tracking-normal">
        Rede não se exclui. Sem clínica ela não compartilha nada e aparece em Vazias; para reativar,
        adicione clínicas.
      </p>
    </FormBlock>
  )
}

/**
 * A busca entre as clínicas sem rede, com as dez primeiras à vista. O que já foi escolhido nesta
 * tela some da lista; o resto é a resposta da API, que já exclui quem pertence a uma rede.
 */
function ClinicPicker({
  excludedIds,
  onPick,
  onClose,
}: {
  excludedIds: string[]
  onPick: (clinic: AdminClinic) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim())
  const query = useClinics({
    filter: 'no_network',
    search: debouncedSearch || undefined,
    page: 1,
    perPage: 10,
  })
  const candidates = (query.data?.data ?? []).filter((clinic) => !excludedIds.includes(clinic.id))

  return (
    <div className="border-hairline bg-pampas border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="eyebrow">Clínicas sem rede</div>
        <button
          type="button"
          onClick={onClose}
          className="font-body text-eyebrow text-label hover:text-ink max-desk:min-h-touch cursor-pointer font-light tracking-normal"
        >
          fechar
        </button>
      </div>
      <SearchInput
        className="mt-3"
        value={search}
        onChange={setSearch}
        placeholder="Buscar por nome ou documento"
        aria-label="Buscar clínica sem rede"
        autoFocus
      />
      <div className="mt-3">
        {query.isError ? (
          <p className="font-body text-body text-plum font-light">
            {errorMessage(query.error)}{' '}
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="hover:text-ink cursor-pointer underline underline-offset-4"
            >
              Tentar de novo
            </button>
          </p>
        ) : query.isPending ? (
          <div role="status" aria-label="Carregando" className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : candidates.length === 0 ? (
          <EmptyState
            title={
              debouncedSearch
                ? `Nenhuma clínica sem rede para "${debouncedSearch}"`
                : 'Nenhuma clínica sem rede'
            }
            description="Quem já está em outra rede sai de lá antes de entrar aqui: abra a rede dela e remova a clínica."
          />
        ) : (
          <ListRows role="list" aria-label="Clínicas sem rede">
            {candidates.map((clinic) => (
              <ListRow
                key={clinic.id}
                role="listitem"
                title={clinicLabel(clinic)}
                meta={clinicMeta(
                  {
                    id: clinic.id,
                    legalName: clinic.legalName,
                    tradeName: clinic.tradeName,
                    city: clinic.address?.city ?? '',
                    state: clinic.address?.state ?? '',
                    status: clinic.status,
                  },
                  formatDocNumber(clinic.docNumber, clinic.docType),
                )}
                trailing={
                  <button
                    type="button"
                    onClick={() => onPick(clinic)}
                    className="font-body text-eyebrow text-label hover:text-ink max-desk:min-h-touch cursor-pointer font-light tracking-normal"
                  >
                    adicionar
                  </button>
                }
              />
            ))}
          </ListRows>
        )}
      </div>
    </div>
  )
}
